import { Accept, useDropzone } from 'react-dropzone';
import { AnnotatePhotoModal, ConfirmModal, EditPhotoModal, ServiceNotesModal } from "#/organisms";
import { Button, ButtonsGroup, Empty, Heading, Icon, InputSelect, Typo } from "#/atoms";
import { DESKTOP_SPACING, HPOSITION, NATURAL } from "@/theme";
import { FC, KeyboardEvent, memo, useCallback, useEffect, useState } from "react";
import { ORDER_STATE, OrderFile } from "@/types";
import { PhotoFile, PhotoUploaderProps } from "./OrderService.interface";
import { StyledAcceptButton, StyledAnnotationLabel,  StyledCounter, StyledDropzone, StyledEditButton, StyledExpandButton, StyledFullscreen, StyledImage, StyledImageSection, StyledNextButton, StyledPhotoImg, StyledPhotos, StyledPreviousButton, StyledThumb } from "./OrderService.styled";

import AnnotationIcon from "@/assets/icons/Annotation";
import Downloader from '@/components/pages/Downloader/Downloader';
import ExpandIcon from '@/assets/icons/Expand';
import { useApi } from '@/api';
import { useModal } from "@/hooks";

const PhotoUplader : FC<PhotoUploaderProps> = ({
    amount = 0,
    originalAmount = 0,
    files,
    isAdditional,
    isAdmin,
    isDraft,
    isPlan = false,
    isWorker,
    title,
    notifications,
    onConfirmFiles,
    onChooseDraftFiles,
    onDeleteFiles,
    onDownloadFiles,
    onMoveFiles,
    onOpenFloorPlanSurcharge,
    onSetSelectedFiles,
    onUploadFiles,
    orderPrice = 0,
    orderState,
    outtakesMode,
    serviceId
}) => {

  const { showModal: showEditPhotoModal, onCloseModal: onEditPhotoClose, onOpenModal: onEditPhotoOpen } = useModal();
  const { showModal: showAnnotatePhotoModal, onCloseModal: onAnnotatePhotoClose, onOpenModal: onAnnotatePhotoOpen } = useModal();
  const { showModal: showConfirmPhotoModal, onCloseModal: onConfirmPhotoClose, onOpenModal: onconfirmPhotoOpen } = useModal();
  const { showModal: showConfirmDraftPhotoModal, onCloseModal: onConfirmDraftPhotoClose, onOpenModal: onconfirmDraftPhotoOpen } = useModal();
  const { showModal: showSpecialRequestModal, onCloseModal: onSpecialRequestModalClose, onOpenModal: onSpecialRequestModalOpen } = useModal();

  const defaultSelectedFiles =  files.filter(f => f.fileStatus === 1).map(f => f.uploadFileId);

  const [tempFiles, setTempFiles] = useState<PhotoFile[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [imageToEdit, setImageToEdit] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState<OrderFile | null>(null);
  const [showDownloader, setShowDownloader] = useState(false);
  const ref = (element: HTMLDivElement) => element?.focus();

  const { session } = useApi();
  const upsellPrice = session.user?.userUpsellPrice || 5;

  useEffect(() => {
    if (orderState === ORDER_STATE.COMPLETE)
      setSelectedFiles(defaultSelectedFiles);
  }, [])

  const handleSelectFile = useCallback((checked: boolean, value: string) => {
    const selValue = (state: string[]) => {
      if (checked) {
          return [...state, value];
      }

      return state.filter((item) => item !== value);
      };  
    
      onSetSelectedFiles?.(selValue);
      setSelectedFiles(selValue);
  }, [selectedFiles]);

  const handleSelectAll = useCallback(() => {
    if (!files) {
      return;
    }

    if (files.length === selectedFiles.length) {
      setSelectedFiles([]); 
      onSetSelectedFiles?.([]);
    }
    else {
      const selFiles = files.map((item) => item.uploadFileId);
        setSelectedFiles(selFiles);
        onSetSelectedFiles?.(selFiles);
    }
  }, [files, selectedFiles]);

  const handleDeleteFiles = useCallback(() => {
    onDeleteFiles?.(serviceId, selectedFiles, isDraft)
  }, [files, selectedFiles]);

  const handleDownloadFiles = useCallback(() => {
    setShowDownloader(true);
    setTimeout(() => {
        setShowDownloader(false);
    }, 12000);
    onDownloadFiles(serviceId, selectedFiles.length > 0 ? selectedFiles : [], isDraft)
  }, [files, selectedFiles, serviceId]);

  const handleMoveFiles = useCallback(() => {
    onMoveFiles?.(serviceId, selectedFiles, isDraft)
  }, [files, selectedFiles]);

  const handleFullscreenOpen = useCallback((id: string) => {
    const file = files.find((item) => item.uploadFileId === id);

    if (file) {
      setFullscreen(file);
    }
  }, [files]);

  const switchPhoto = useCallback((step: number) => {
    
    if (!fullscreen) return;
    const index = files.findIndex(f => f.uploadFileId === fullscreen.uploadFileId);

    const newIndex = index + step;

    if (newIndex >= 0 && newIndex < files.length) {
      setFullscreen(files[newIndex]);
    }

  }, [files, fullscreen]);

  const handleKeyDown = useCallback((e : KeyboardEvent<HTMLDivElement>) => {
    switch(e.code) {
      case "Escape":
        setFullscreen(null);
        break;
      case "ArrowLeft":
        switchPhoto(-1);
        break;
      case "ArrowRight":
        switchPhoto(1);
        break;
    }
  }, [files, fullscreen]);

  const handleFullscreenButton = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>, step: number) => {
    e.preventDefault();
    e.stopPropagation();
    
    switchPhoto(step);

  }, [files, fullscreen]);

  const handleFullscreenClose = useCallback(() => {
    setFullscreen(null);
  }, []);

  const handleAnnotatePhoto = useCallback(() => {
    if (selectedFiles.length !== 1)
      return;

    const url = files.filter(f => f.uploadFileId === selectedFiles[0])?.[0].thumbURL;
    setImageToEdit(url);
    onAnnotatePhotoOpen();
  }, [selectedFiles]);

  const handleChoose = useCallback(() => {
    if (selectedFiles.length > 0)
      onconfirmDraftPhotoOpen();
  }, [selectedFiles]);

  const handleChosen = useCallback(() => {
    if (selectedFiles.length > 0)
      onChooseDraftFiles?.(selectedFiles)
  }, [selectedFiles, serviceId]);

  const handleConfirmed = useCallback(() => {
    if (selectedFiles.length > 0)
      onConfirmFiles?.(serviceId, selectedFiles, isDraft)
  }, [selectedFiles, serviceId]);
  
  const handleSavePhoto = useCallback(() => {
    onEditPhotoClose();
  }, []);
  
    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        setTempFiles(acceptedFiles.map((file) =>
            Object.assign(file, {
                id: file.name,
                preview: URL.createObjectURL(file),
            })
        ));
        // setTimeout(async () => {
        await onUploadFiles?.(serviceId, acceptedFiles, isDraft);
        setTempFiles([]);
        // }, 100);
    }, [isDraft, serviceId]);

 const accept : Accept = isPlan 
    ? {
        //   '*': [],
    } 
    : {
        'image/*': ['.png', '.jpg', '.gif'],
    };

  const {
    getRootProps,
    getInputProps,
    open, 
    } = useDropzone({
        accept: accept,
        noClick: true,
        onDrop
    });

  const getThumb = (id: string, img: string, hasAnnotation: boolean, fileStatus: number, index: number) => {
    const checked = selectedFiles.includes(id);

    return (
      <>
        {fileStatus === 1 && <StyledEditButton></StyledEditButton> }
        <StyledAcceptButton type="button">
          <InputSelect type="checkbox" checked={checked} value={id} onChangeValue={handleSelectFile} labelPosition={HPOSITION.CENTER} />
        </StyledAcceptButton>
        {hasAnnotation && <StyledAnnotationLabel><AnnotationIcon /></StyledAnnotationLabel>}
        <StyledCounter>{index + 1}</StyledCounter>
        <StyledExpandButton type="button" onClick={() => handleFullscreenOpen(id)}><ExpandIcon /></StyledExpandButton>
        <StyledPhotoImg img={img} />
      </>
    )
  }

  const getFilesThumb = (data: PhotoFile[]) => {
    return data.map(({ id, preview }) => {
      return (
        <StyledThumb key={id}>
          <StyledPhotoImg img={preview} />
        </StyledThumb>
      )
    });
  }

  const getUploadedThumb = (data: OrderFile[]) => {
    return data.map(({ uploadFileId, thumbURL, hasAnnotation, fileStatus }, index) => {
      return (
        <StyledThumb key={uploadFileId}>
          {getThumb(uploadFileId, thumbURL, hasAnnotation, fileStatus, index)}
        </StyledThumb>
      )
    });
  }

  const getSelectionText = () => selectedFiles.length === 0 ? 'All': 'Selected';
  const itemText = isPlan ? 'Floor Plans' : 'Photos'; 
  const lowercaseItemText = itemText.toLowerCase();

  const getConfirmModalText = () => {
    const alreadyUsed = originalAmount && originalAmount !== amount ? originalAmount - amount : 0;
    const allSelected = selectedFiles.length + alreadyUsed;
    const amountToUse = originalAmount || amount;

    if (allSelected < amountToUse ) {
      return `You've chosen only ${allSelected} photo(s) from the whole package. You can also add ${amountToUse - allSelected} photos to the chosen ones. In total you will pay ${session.getUserCurrency()}${orderPrice}`;
    }
    else if (allSelected > amountToUse ) {
      return `We will add to your invoice ${session.getUserCurrency()}${(allSelected - amountToUse) * upsellPrice} for ${allSelected - amountToUse} extra photos. In total you will pay ${session.getUserCurrency()}${orderPrice + (allSelected - amountToUse) * upsellPrice}`;
    }
    else 
     return `You are about to confirm selected photos. In total you will pay ${session.getUserCurrency()}${orderPrice}`
  }

  const getConfirmDraftModalText = () => {
    return `You have chosen ${selectedFiles.length} photo(s) for further procressing. Continue?`;

  }

  return (
    <>
    <StyledImageSection>
     <StyledDropzone {...getRootProps({ className: isAdmin? 'dropzone admin' : 'dropzone' })}>
        <input type="file" {...getInputProps()} />
        { title && <Heading scale="h4" margin={`0 0 ${DESKTOP_SPACING.XXXL}px`}>{title}</Heading> }
        {isAdditional && <Typo margin='10px'>You can purchase additional photos at any time</Typo> }
        {!isDraft && !isPlan && orderState === ORDER_STATE.COMPLETE && <Typo margin='10px'>Please select your favourite {amount} photos to confirm and make them available to download. You can also select and download more images if you require and we will simply add them to your order ({session.getUserCurrency()} 5 per photo)</Typo> }
        {isDraft && !isWorker && !isPlan  && <Typo margin='10px'>You can select additional photos for further processing. Note that exceeding the amount included in the package may incur further charges.</Typo> }
        {files.length === 0 ? (
        <Empty>No {lowercaseItemText} are added yet. You can start adding new {lowercaseItemText}.</Empty>
        ) : (
        <StyledPhotos>
            {getFilesThumb(tempFiles)}
            {getUploadedThumb(files)}
        </StyledPhotos>
        )}
    </StyledDropzone>
    { !isDraft && orderState === ORDER_STATE.COMPLETE && <Heading scale='h3' margin='10px'>You have chosen {Math.min(selectedFiles.length, amount)} of {amount} package photos {selectedFiles.length > amount && `and ${selectedFiles.length - amount} additional`}</Heading> }
    { isAdditional && <Heading scale='h3' margin='10px'>You have chosen {selectedFiles.length} additional photos</Heading> }
    {!outtakesMode && <ButtonsGroup flexWrap="wrap" margin="20px 0px 0px"  fullWidth>
        { (!isDraft || isAdmin) && <Button actionType="outline" onClick={handleSelectAll}>{ selectedFiles.length === files.length ? 'Deselect All' : 'Select All' } {itemText}</Button> }
        { isAdmin && <Button actionType="outline" onClick={handleDeleteFiles}>Delete Selected {itemText}</Button> }
        { (!isDraft || isAdmin) && !isAdditional && (orderState === ORDER_STATE.CONTENT_SELECTED || isPlan || isAdmin) && <Button actionType="outline" onClick={handleDownloadFiles}>Download {getSelectionText()} {itemText}</Button>}
        {!isDraft && isPlan && isAdmin && <Button actionType="danger" onClick={onOpenFloorPlanSurcharge}>Total Sq Ft</Button>}
        { (isAdmin || isWorker) && <Button onClick={open}>Upload New {itemText}</Button> }
        {isDraft && isAdmin && <Button onClick={handleMoveFiles}>Move {getSelectionText()}</Button>}
        {!isDraft &&  (orderState || 99) < ORDER_STATE.CONTENT_SELECTED && <Button onClick={onSpecialRequestModalOpen}>{isAdmin ? 'Notes' : 'Special Request' } </Button>}
        {(!isDraft && !isPlan &&  orderState === ORDER_STATE.COMPLETE || isAdditional) && <Button actionType='solid' buttonColor='secondary'  onClick={onconfirmPhotoOpen}>Confirm</Button>}
        {!isDraft && selectedFiles.length === 1 && <Button actionType="danger" onClick={handleAnnotatePhoto}>{itemText} Amendment</Button>}
        {isDraft && !isWorker && !isAdmin && <Button onClick={handleChoose}>Choose</Button>}
    </ButtonsGroup> }
    {fullscreen && (
    <StyledFullscreen ref={ref} onClick={handleFullscreenClose} onKeyDown={handleKeyDown} tabIndex={0}>
        <Button actionType="ghost" onClick={handleFullscreenClose}><Icon type="cross" fillColor={NATURAL.A} /></Button>
        <StyledImage src={fullscreen.thumbURL} />
        { files.findIndex(f => fullscreen.uploadFileId === f.uploadFileId) > 0 && <StyledPreviousButton onClick={(e) => { handleFullscreenButton(e, -1);}}>&lt;</StyledPreviousButton> }
        { files.findIndex(f => fullscreen.uploadFileId === f.uploadFileId) < files.length - 1 && <StyledNextButton onClick={(e) => { handleFullscreenButton(e, 1);}}>&gt;</StyledNextButton> }
    </StyledFullscreen>
    )}
    {showEditPhotoModal && imageToEdit && (
    <EditPhotoModal
        imageUrl={imageToEdit}
        loading={false}
        onClose={onEditPhotoClose}
        onSavePhoto={handleSavePhoto}
    />
    )}
    {showAnnotatePhotoModal && imageToEdit && (
    <AnnotatePhotoModal
        imageUrl={imageToEdit}
        loading={false}
        onClose={onAnnotatePhotoClose}
        uploadFileId={selectedFiles[0]}
    />
    )}
    {showConfirmPhotoModal && onConfirmFiles && (
    <ConfirmModal
        description={getConfirmModalText()}
        onConfirm={handleConfirmed}
        onClose={onConfirmPhotoClose}
        showCancel={true}
        title="Purchase Confirmation"
        width='600px'
    />
    )}
    {showConfirmDraftPhotoModal && onChooseDraftFiles && (
    <ConfirmModal
        description={getConfirmDraftModalText()}
        onConfirm={handleChosen}
        onClose={onConfirmDraftPhotoClose}
        showCancel={true}
        title="Selection confirmation"
        width='600px'
    />
    )}
    {showSpecialRequestModal && (
    <ServiceNotesModal
        isCustomer={true}
        notifications={notifications ?? []}
        onClose={onSpecialRequestModalClose}
        onConfirm={onSpecialRequestModalClose}
        orderServiceId={serviceId}
        title="Edit Request"
    />
    )}
  { showDownloader &&  <Downloader /> }
  </StyledImageSection>
  </>
);
}

export default memo(PhotoUplader);